import { useRef } from 'react';
import gql from 'graphql-tag';
import { type ApolloClient, type ApolloError } from 'apollo-client';
import { useQuery, type QueryHookOptions } from '@apollo/react-hooks';
import { type GQLCustomUITunnelDefinition, type GQLAppTunnels } from '../graphql/types';
import { createAtlAttributionHeader } from '../../utils';
import type { DocumentNode } from 'graphql';
import { fg } from '@atlaskit/platform-feature-flags';
import { useLazyLoadQuery, graphql } from 'react-relay';
import type { useCustomUITunnelsListRelay_forge_ui_Query as ActiveTunnelsRelayQuery } from './__generated__/useCustomUITunnelsListRelay_forge_ui_Query.graphql';

export const getActiveTunnelsQuery = (): DocumentNode => {
	const query = `
  query forge_ui_getActiveTunnels($appId: ID!, $environmentId: ID!) {
    appActiveTunnels(appId: $appId, environmentId: $environmentId) {
      customUI {
        resourceKey
        tunnelUrl
      }
    }
  }
  `;

	return gql`
		${query}
	`;
};

export interface QueryVariables {
	appId: string;
	environmentId: string;
}

export interface QueryData {
	appActiveTunnels: GQLAppTunnels;
}

export type UseAppTunnelsQueryHookOptions = QueryHookOptions<QueryData, QueryVariables>;

export type UseAppTunnelsApolloOptions = QueryVariables & {
	client?: ApolloClient<object>;
	contextIds?: string[];
	environmentType: string;
	expandAppOwner?: boolean;
	queryOptions?: UseAppTunnelsQueryHookOptions;
};

export type UseAppTunnelsRelayOptions = QueryVariables & {
	contextIds?: string[];
};

/**
 * @deprecated Use UseAppTunnelsApolloOptions or UseAppTunnelsRelayOptions instead
 */
export type UseAppTunnelsOptions = UseAppTunnelsApolloOptions;

interface CommonHookResult {
	error?: ApolloError;
	loading: boolean;
}

export interface UseAppTunnelsHookResult extends CommonHookResult {
	tunnels: GQLCustomUITunnelDefinition[];
}

export interface AppTunnelsGQLResult {
	data?: {
		appActiveTunnels?: {
			customUI?: Array<GQLCustomUITunnelDefinition>;
		};
	};
	loading: boolean;
}

export const createAppTunnelsQueryOptions = (
	client: ApolloClient<object> | undefined,
	appId: string,
	environmentId: string,
	queryOptions?: UseAppTunnelsQueryHookOptions,
): UseAppTunnelsQueryHookOptions => {
	return {
		variables: {
			appId,
			environmentId,
		},
		...queryOptions,
		query: getActiveTunnelsQuery(),
		client,
		/**
		 * We don't want to set 'ignore' because that discards errors and we need to inspect
		 * them for SLI reporting. We also need 'all' so we don't discard data on errors:http://go/j/AUX-317
		 * https://www.apollographql.com/docs/react/data/error-handling/
		 */
		errorPolicy: 'all',
		context: {
			headers: {
				'atl-attribution': createAtlAttributionHeader(),
			},
		},
	};
};

export const getCustomUITunnelsFromGQLResult = ({
	loading,
	data,
}: AppTunnelsGQLResult): GQLCustomUITunnelDefinition[] | undefined => {
	if (!loading) {
		return data?.appActiveTunnels?.customUI;
	}

	return undefined;
};

export const useCustomUITunnelsListApollo = (
	options: UseAppTunnelsApolloOptions,
): UseAppTunnelsHookResult => {
	const { appId, environmentType, environmentId, client, queryOptions } = options;

	const shouldQuery = useRef(true);

	if (environmentType !== 'DEVELOPMENT' || fg('forge-ui-add-relay-tunnels-query')) {
		shouldQuery.current = false;
	}

	const requestOptions = createAppTunnelsQueryOptions(client, appId, environmentId, queryOptions);

	const { error, loading, data } = useQuery<QueryData, QueryVariables>(
		requestOptions.query || getActiveTunnelsQuery(),
		{
			...requestOptions,
			skip: !shouldQuery.current,
		},
	);
	const tunnels = getCustomUITunnelsFromGQLResult({ loading, data });

	return {
		error,
		tunnels: tunnels || [],
		loading,
	};
};

export const useCustomUITunnelsListRelay = (options: UseAppTunnelsRelayOptions): UseAppTunnelsHookResult => {
	const { appId, environmentId } = options;
	const contextIds = options.contextIds ?? [];

	/* eslint-disable @atlassian/relay/unused-fields, @atlassian/relay/no-wrapping-hooks */
	const data = useLazyLoadQuery<ActiveTunnelsRelayQuery>(
		graphql`
			query useCustomUITunnelsListRelay_forge_ui_Query(
				$appId: ID!
				$environmentId: ID!
			) {
				appActiveTunnels(appId: $appId, environmentId: $environmentId) {
					customUI {
						resourceKey
						tunnelUrl
					}
				}
			}
		`,
		{
			appId,
			environmentId,
		},
		{
			networkCacheConfig: {
				metadata: {
					headers: {
						'atl-attribution': createAtlAttributionHeader(contextIds.sort().join()),
					},
				},
			},
		},
	);
	/* eslint-enable @atlassian/relay/unused-fields, @atlassian/relay/no-wrapping-hooks */

	const tunnels = (data?.appActiveTunnels?.customUI ?? []) as GQLCustomUITunnelDefinition[];

	return {
		tunnels,
		loading: false,
	};
};

